import { Dictionary, EN_US } from "./dictionary";
import { ES_MX } from "./locales/es-MX";
import { FR_CA } from "./locales/fr-CA";

/**
 * Single-process locale state for widget UI strings. The widget runtime
 * calls `setLocale` once at boot (with the operator default or the
 * navigator language); components read translations via `t()` directly
 * — no Provider / hook so far since the locale doesn't change after
 * mount. When mid-session locale switching lands, wrap this in a
 * subscription + useSyncExternalStore.
 *
 * Unknown locales fall back to en-US so a missing dictionary never
 * crashes the widget — visitors just see English copy. Variants of the
 * registered languages (es-ES, fr-CA, etc.) match via the language-only
 * fallback in `setLocale`.
 */
const REGISTRY: Record<string, Dictionary> = {
  "en-US": EN_US,
  "es-MX": ES_MX,
  "fr-CA": FR_CA
};

let current: Dictionary = EN_US;
let currentTag = "en-US";

/**
 * Pick the closest match — exact ("en-US"), language-only ("en"), or
 * en-US fallback. Operator default beats navigator.language; both can
 * pass through here. Updates both the active dictionary AND the active
 * BCP-47 tag so `getActiveLocale()` can hand the same locale to
 * `Intl.NumberFormat` / `toLocaleDateString` callers.
 */
export function setLocale(locale: string | null | undefined): void {
  if (!locale) {
    current = EN_US;
    currentTag = "en-US";
    return;
  }
  const normalized = locale.replace(/_/g, "-");
  if (REGISTRY[normalized]) {
    current = REGISTRY[normalized];
    currentTag = normalized;
    return;
  }
  const language = normalized.split("-")[0];
  for (const key of Object.keys(REGISTRY)) {
    if (key.startsWith(`${language}-`)) {
      current = REGISTRY[key];
      currentTag = key;
      return;
    }
  }
  current = EN_US;
  currentTag = "en-US";
}

/**
 * The active BCP-47 locale tag (e.g. "fr-CA"). Hand to `Intl.*` /
 * `toLocaleDateString` so formatted dates + numbers follow the same
 * locale as the dictionary lookup.
 */
export function getActiveLocale(): string {
  return currentTag;
}

/**
 * Look up a key in the active dictionary. {placeholder} substitution
 * happens here so callers don't replicate string-formatting logic.
 * Missing keys fall back to the en-US value (or the key itself if even
 * en-US doesn't have it — surfaces translation bugs as the operator can
 * spot the raw key in production).
 */
export function t(key: keyof Dictionary, replacements?: Record<string, string | number>): string {
  const raw = current[key] ?? EN_US[key] ?? String(key);
  if (!replacements) return raw;
  return raw.replace(/\{(\w+)\}/g, (_, name) => {
    const value = replacements[name];
    return value === undefined ? `{${name}}` : String(value);
  });
}

/** Register a locale at runtime — used by lazy-loaded locale bundles. */
export function registerLocale(locale: string, dictionary: Dictionary): void {
  REGISTRY[locale] = dictionary;
}
